import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, MapPin, Edit, Trash, Plus, Download, Check } from 'lucide-react';
import "../styles/reservationStyle.css"; // Asegúrate de que la ruta a tu CSS es correcta
import parkingService from '../services/parkingService';
import reservationService from '../services/reservationsService';
import adminService from '../services/adminServices';
import vehicleService from '../services/vehicleService';

export default function Gestion_de_reservas() {
  const [registeredParkings, setRegisteredParkings] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date(2025, 2, 11));
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [entryTime, setEntryTime] = useState('');
  const [exitTime, setExitTime] = useState('');
  const [selectedCar, setSelectedCar] = useState('');
  const [selectedParkingId, setSelectedParkingId] = useState('');
  const [currentParking, setCurrentParking] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingReservationId, setEditingReservationId] = useState(null);
  const imageRef = useRef(null);

  // Estados mejorados
  const [cedula, setCedula] = useState('');
  const [userVehicles, setUserVehicles] = useState([]); // Ahora dinámico
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [isSearchingVehicles, setIsSearchingVehicles] = useState(false);

  // Carga inicial de parqueos y reservas
  useEffect(() => {
    loadParkings();
    loadReservations();
  }, []);

  // Efecto para cargar vehículos automáticamente cuando cambia la cédula
  useEffect(() => {
    if (cedula && cedula.length >= 9) { // Validar que la cédula tenga al menos 9 caracteres
      loadUserVehiclesByCedula(cedula);
    } else {
      // Limpiar vehículos si la cédula no es válida
      setUserVehicles([]);
      setSelectedCar('');
    }
  }, [cedula]);

  // Cargar parqueo seleccionado
  useEffect(() => {
    if (selectedParkingId && registeredParkings.length > 0) {
      const parking = registeredParkings.find(p => p._id === selectedParkingId);
      setCurrentParking(parking || null);
      setSelectedSpot(null);
    } else {
      setCurrentParking(null);
    }
  }, [selectedParkingId, registeredParkings]);

  // --- FUNCIÓN PARA CARGAR VEHÍCULOS POR CÉDULA ---
  const loadUserVehiclesByCedula = async (userCedula) => {
    try {
      setLoadingVehicles(true);
      console.log('Buscando vehículos para cédula:', userCedula);

      const response = await vehicleService.getUserVehicles(userCedula);
      console.log('Respuesta del backend:', response);

      // El backend devuelve { vehicles: [array_of_plates] }
      const vehiclePlates = response?.vehicles || [];

      // Convertir array de placas a objetos con formato esperado
      const vehicleObjects = vehiclePlates.map(plate => ({ plate }));

      setUserVehicles(vehicleObjects);

      // Si hay vehículos y no hay uno seleccionado, seleccionar el primero
      if (vehicleObjects.length > 0 && !selectedCar) {
        setSelectedCar(vehicleObjects[0].plate);
      }

      console.log('Vehículos cargados:', vehicleObjects);

    } catch (error) {
      console.error('Error al cargar vehículos del usuario:', error);
      setUserVehicles([]);

      // Solo mostrar alert si no es un error 404 (usuario no encontrado)
      if (error.response?.status !== 404) {
        alert('Error al cargar los vehículos del usuario. Verifique la cédula.');
      }
    } finally {
      setLoadingVehicles(false);
    }
  };

  // --- FUNCIONES DE CARGA DE DATOS (CONEXIÓN AL BACKEND) ---
  const loadParkings = async () => {
    try {
      setIsLoading(true);
      const response = await parkingService.getAllParkings();
      setRegisteredParkings(response.parkings || response || []);
    } catch (error) {
      console.error('Error al cargar parqueos:', error);
      alert('Error al cargar los parqueos. Por favor, revise la consola.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadReservations = async () => {
    try {
      setIsLoading(true);
      const response = await reservationService.getAllReservationsAd();
      const reservationsArray = Array.isArray(response) ? response :
        Array.isArray(response?.reservations) ? response.reservations :
          [];

      setReservations(reservationsArray);

    } catch (error) {
      console.error('Error al cargar reservas:', error);
      alert('Error al cargar las reservas. Por favor, revise la consola.');
      setReservations([]); // Asegurar que siempre sea un array
    } finally {
      setIsLoading(false);
    }
  };

  // --- FUNCIONES DE GESTIÓN DE RESERVAS (CONEXIÓN AL BACKEND) ---
  const handleCreateReservation = async () => {
    if (!currentParking || !selectedSpot || !entryTime || !exitTime || !cedula || !selectedCar) {
      alert('Por favor complete todos los campos: Cédula, vehículo, parqueo, campo, fecha y horas.');
      return;
    }

    const reservationData = {
      userCedula: cedula,
      parking: selectedParkingId,
      spotId: selectedSpot,
      date: selectedDate.toISOString().split('T')[0],
      entryTime,
      exitTime,
      vehiclePlate: selectedCar
    };

    try {
      setIsLoading(true);
      await reservationService.createReservationByParkingId(selectedParkingId, reservationData);
      alert('Reserva creada exitosamente');
      await loadReservations();
      resetForm();
    } catch (error) {
      console.error('Error al crear reserva:', error);
      alert('Error al crear la reserva. Verifique los datos e intente de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateReservation = async () => {
    if (!currentParking || !selectedSpot || !entryTime || !exitTime || !cedula || !selectedCar) {
      alert('Por favor complete todos los campos.');
      return;
    }

    const updateData = {
      userCedula: cedula,
      parking: selectedParkingId,
      spotId: selectedSpot,
      date: selectedDate.toISOString().split('T')[0],
      entryTime,
      exitTime,
      vehiclePlate: selectedCar
    };

    try {
      setIsLoading(true);
      await adminService.editReservation(editingReservationId, updateData);
      alert('Reserva actualizada exitosamente');
      await loadReservations();
      cancelEdit();
    } catch (error) {
      console.error('Error al actualizar reserva:', error);
      alert('Error al actualizar la reserva. Verifique los datos.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteReservation = async (reservationId) => {
    if (window.confirm('¿Está seguro de que desea eliminar esta reserva?')) {
      try {
        setIsLoading(true);
        await reservationService.cancelReservation(reservationId);
        alert('Reserva eliminada exitosamente');
        await loadReservations();
      } catch (error) {
        console.error('Error al eliminar reserva:', error);
        alert('Error al eliminar la reserva.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // --- FUNCIONES AUXILIARES (LÓGICA DEL FORMULARIO) ---
  const handleEditReservation = (reservation) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const parkingId = reservation.parking?._id || '';
    const vehiclePlate = reservation.vehicle?.plate || '';
    const userCedula = reservation.user?.idCard || reservation.user?.cedula || '';

    setIsEditing(true);
    setEditingReservationId(reservation._id);
    setSelectedParkingId(parkingId);
    setSelectedSpot(reservation.spotId);
    setSelectedDate(new Date(reservation.date));
    setEntryTime(reservation.entryTime);
    setExitTime(reservation.exitTime);
    setSelectedCar(vehiclePlate);
    setCedula(userCedula);
  };

  const resetForm = () => {
    setSelectedSpot(null);
    setEntryTime('');
    setExitTime('');
    setSelectedParkingId('');
    setCurrentParking(null);
    setCedula('');
    setSelectedCar('');
    setUserVehicles([]);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditingReservationId(null);
    resetForm();
  };

  // Función para manejar el cambio de cédula
  const handleCedulaChange = (e) => {
    const newCedula = e.target.value;
    setCedula(newCedula);

    // Limpiar el vehículo seleccionado cuando cambia la cédula
    setSelectedCar('');
  };
  const selectDay = (day) => {
    if (!day) return;
    const newDate = new Date(selectedDate);
    newDate.setDate(day);
    setSelectedDate(newDate);
  };

  // --- FUNCIONES DE UI AUXILIARES ---
  const toggleSpotSelection = (spotId) => {
    const spot = currentParking.spots.find(s => s.id === spotId);
    if (!spot) return;

    const isCurrentlyReservedByThis = isEditing && reservations.find(r =>
      r._id === editingReservationId && r.spotId === spotId
    );

    if (spot.status === 'available' || isCurrentlyReservedByThis) {
      setSelectedSpot(prevSpot => prevSpot === spotId ? null : spotId);
    }
  };

  const getSpotClass = (spot) => {
    const baseClass = 'reservas-spot';
    if (selectedSpot === spot.id) return `${baseClass} reservas-spot-selected`;
    if (spot.status === 'occupied') return `${baseClass} reservas-spot-occupied`;
    return `${baseClass} reservas-spot-available`;
  };

  const changeMonth = (increment) => {
    setSelectedDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() + increment);
      return newDate;
    });
  };

  const selectDate = (day) => {
    setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day));
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];

    // Días vacíos al inicio (ajustar para que lunes sea 0)
    const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1;
    for (let i = 0; i < adjustedFirstDay; i++) {
      days.push(null);
    }

    // Días del mes
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  // Calcular precio total (simplificado)
  const calculateTotalPrice = () => {
    if (!entryTime || !exitTime || !currentParking) return 0;

    const entry = new Date(`2000-01-01T${entryTime}`);
    const exit = new Date(`2000-01-01T${exitTime}`);
    const hours = Math.max(1, Math.ceil((exit - entry) / (1000 * 60 * 60)));

    return hours * (currentParking.pricePerHour || 500);
  };

  const totalPrice = calculateTotalPrice();

  // Funciones auxiliares para el calendario
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(selectedDate);
    const firstDay = getFirstDayOfMonth(selectedDate);
    const days = [];

    // Espacios en blanco para los días antes del primer día del mes
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="reservas-calendar-day-empty"></div>);
    }

    // Días del mes
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(
        <button
          key={day}
          onClick={() => selectDate(day)}
          className={`reservas-calendar-day ${selectedDate.getDate() === day ? 'reservas-calendar-day-selected' : ''}`}
        >
          {day}
        </button>
      );
    }

    return days;
  };


  const searchUserVehicles = async (idCard) => {
    try {
      setIsSearchingVehicles(true);
      const response = await vehicleService.getUserVehicles(idCard);

      if (response && Array.isArray(response)) {
        setUserVehicles(response);
      } else {
        setUserVehicles([]);
      }
    } catch (error) {
      console.error('Error al buscar vehículos:', error);
      setUserVehicles([]);
    } finally {
      setIsSearchingVehicles(false);
    }
  };

  const prepareEdit = async (reservation) => {
    setIsEditing(true);
    setEditingReservationId(reservation._id);
    setCedula(reservation.idCard);
    setSelectedParkingId(reservation.idParking);
    setSelectedSpot(reservation.idSpot);
    setSelectedDate(new Date(reservation.dateIn));
    setEntryTime(reservation.timeIn);
    setExitTime(reservation.timeOut);
    setSelectedCar(reservation.plate);

    if (reservation.idCard) {
      await searchUserVehicles(reservation.idCard);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const getParkingName = (parkingId) => {
    const parking = registeredParkings.find(p => p._id === parkingId);
    return parking ? parking.name || parking.parkingName : 'Parqueo no encontrado';
  };

  return (
    <div className="reservas-main-container">
      <h1 className="reservas-main-title">Gestión de Reservas</h1>

      {/* Edit Reservation Form */}
      <div className="reservas-edit-form-container">


        <div className="reservas-two-column-grid">
          {/* Columna Izquierda - Calendario y Horarios */}
          <div className="reservas-left-column">
            <div className="reservas-edit-form-header">
              <h2 className="reservas-edit-form-title">
                {isEditing ? 'Editar Reservación' : 'Nueva Reservación'}
              </h2>
              {isEditing && (
                <button onClick={cancelEdit} className="reservas-cancel-button">
                  Cancelar
                </button>
              )}
            </div>
            {/* Selector de Parqueo */}
            <div className="reservas-parking-selector-container">
              <label className="reservas-parking-selector-label">
                <MapPin size={16} className="reservas-parking-selector-icon" />
                Seleccionar Parqueo
              </label>
              <select
                value={selectedParkingId}
                onChange={(e) => setSelectedParkingId(e.target.value)}
                className="reservas-parking-selector-select"
              >
                <option value="">Seleccione un parqueo...</option>
                {registeredParkings.map(parking => (
                  <option key={parking._id} value={parking._id}>
                    {parking.parkingName} - ₡{parking.tarify} por hora
                  </option>
                ))}
              </select>
            </div>


            {/* Mapa del Parqueo */}
            {currentParking ? (
              <div className="reservas-parking-map-container">
                <h3 className="reservas-parking-map-title">
                  {currentParking.parkingName}
                </h3>
                <div className="reservas-parking-map-wrapper">
                  <img
                    ref={imageRef}
                    src={currentParking.image}
                    alt={`Mapa de ${currentParking.parkingName}`}
                    className="reservas-parking-map-image"
                  />
                  {currentParking.spots.map(spot => (
                    <div
                      key={spot.id}
                      className={`reservas-parking-spot ${selectedSpot === spot.id ? 'reservas-parking-spot-selected' :
                        spot.status === 'available' ? 'reservas-parking-spot-available' :
                          spot.status === 'occupied' ? 'reservas-parking-spot-occupied' :
                            spot.status === 'reserved' ? 'reservas-parking-spot-reserved' :
                              'reservas-parking-spot-disabled'
                        }`}
                      style={{
                        left: `${spot.x}%`,
                        top: `${spot.y}%`,
                        cursor: spot.status === 'available' || selectedSpot === spot.id || (isEditing && spot.status === 'reserved') ? 'pointer' : 'not-allowed'
                      }}
                      onClick={() => toggleSpotSelection(spot.id)}
                    >
                      {spot.label}
                    </div>
                  ))}
                </div>

                {/* Leyenda */}
                <div className="reservas-parking-legend">
                  <div className="reservas-legend-item"><div className="reservas-legend-color-available"></div>Disponible</div>
                  <div className="reservas-legend-item"><div className="reservas-legend-color-occupied"></div>Ocupado</div>
                  <div className="reservas-legend-item"><div className="reservas-legend-color-reserved"></div>Reservado</div>
                  <div className="reservas-legend-item"><div className="reservas-legend-color-selected"></div>Seleccionado</div>
                </div>

                <div className="reservas-parking-info">
                  <div className="reservas-parking-info-content">
                    <span>Tarifa por hora: ₡{currentParking.tarify}</span>
                    <span>Campo seleccionado: {selectedSpot || 'Ninguno'}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="reservas-no-parking-placeholder">
                <MapPin size={48} className="reservas-no-parking-icon" />
                <p className="reservas-no-parking-text">Seleccione un parqueo para ver los campos disponibles</p>
              </div>
            )}

            {/* Horarios */}
            <div className="reservas-time-section">
              <div className="reservas-time-inputs">
                <div>
                  <label className="reservas-time-input-label">Hora de entrada</label>
                  <input type="time" value={entryTime} onChange={(e) => setEntryTime(e.target.value)} className="reservas-time-input" />
                </div>
                <div>
                  <label className="reservas-time-input-label">Hora de salida</label>
                  <input type="time" value={exitTime} onChange={(e) => setExitTime(e.target.value)} className="reservas-time-input" />
                </div>
              </div>
            </div>
          </div>

          {/* Columna Derecha - Mapa y Información */}
          <div className="reservas-right-column">
            <div className="reservas-calendar-container">
              <label className="reservas-calendar-label">Día de reserva</label>
              <div className="ths-calendar-wrapper">
                <div className="ths-calendar-header">
                  <button
                    type="button"
                    onClick={() => changeMonth(-1)}
                    className="ths-calendar-nav-button"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="ths-calendar-month-year">
                    {monthNames[selectedDate.getMonth()]} {selectedDate.getFullYear()}
                  </span>
                  <button
                    type="button"
                    onClick={() => changeMonth(1)}
                    className="ths-calendar-nav-button"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
                <div className="ths-calendar-grid">
                  {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(day => (
                    <div key={day} className="ths-calendar-day-header">{day}</div>
                  ))}
                  {getDaysInMonth(selectedDate).map((day, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => selectDay(day)}
                      disabled={!day}
                      className={`ths-calendar-day-button ${day === selectedDate.getDate() ? 'selected' : ''
                        }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Información del Vehículo */}
            <div className="reservas-vehicle-section">
              <label className="reservas-vehicle-label">Ingrese un número de identificación</label>
              <input value={cedula} onChange={(e) => setCedula(e.target.value)} className="reservas-vehicle-select" placeholder="Cédula del cliente" />
              <label className="reservas-vehicle-label">Seleccione un vehículo</label>
              <select value={selectedCar} onChange={(e) => setSelectedCar(e.target.value)} className="reservas-vehicle-select">
                <option value="">Seleccione un vehículo...</option>
                {userVehicles.map(vehicle => (
                  <option key={vehicle.plate} value={vehicle.plate}>{vehicle.plate}</option>
                ))}
              </select>
            </div>

            {/* Pago Total y Botón de Acción */}
            <div className="reservas-payment-section">
              <div className="reservas-payment-label">Pago total</div>
              <div className="reservas-payment-amount">₡ {totalPrice}</div>
              <button
                onClick={isEditing ? handleUpdateReservation : handleCreateReservation}
                disabled={!currentParking || !selectedSpot || isLoading}
                className={`reservas-action-button ${(!currentParking || !selectedSpot || isLoading) ? 'reservas-action-button-disabled' : 'reservas-action-button-enabled'}`}
              >
                {isLoading ? 'Procesando...' : isEditing ? 'Actualizar Reserva' : 'Realizar Reserva'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="reservas-form-section">
        {/* Registered Reservations */}
        <div className="reservas-table-container">
          <h2 className="reservas-edit-form-title">Lista de reservas</h2>
          <div className="reservas-table-wrapper">
            <table className="reservas-user-table">
              <thead className="reservas-table-header">
                <tr>
                  <th className="reservas-table-th">Cédula</th>
                  <th className="reservas-table-th">Placa</th>
                  <th className="reservas-table-th">Parqueo</th>
                  <th className="reservas-table-th">Campo</th>
                  <th className="reservas-table-th">Fecha</th>
                  <th className="reservas-table-th">Entrada</th>
                  <th className="reservas-table-th">Salida</th>
                  <th className="reservas-table-th">Estado</th>
                  <th className="reservas-table-th reservas-text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {reservations.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="reservas-table-td reservas-text-center" style={{ padding: '2rem', color: '#9ca3af', fontStyle: 'italic' }}>
                      {isLoading ? 'Cargando reservas...' : 'No hay reservas registradas'}
                    </td>
                  </tr>
                ) : (
                  reservations.map((reservation) => (
                    <tr key={reservation._id}>
                      <td className="reservas-table-td">{reservation.idCard}</td>
                      <td className="reservas-table-td">{reservation.plate}</td>
                      <td className="reservas-table-td">{getParkingName(reservation.idParking)}</td>
                      <td className="reservas-table-td">{reservation.idSpot}</td>
                      <td className="reservas-table-td">{formatDate(reservation.dateIn)}</td>
                      <td className="reservas-table-td">{reservation.timeIn}</td>
                      <td className="reservas-table-td">{reservation.timeOut}</td>
                      <td className="reservas-table-td">{reservation.status || 'Pendiente'}</td>
                      <td className="reservas-table-td reservas-text-center">
                        <div className="reservas-flex-space-x">
                          <button
                            onClick={() => prepareEdit(reservation)}
                            className="reservas-action-icon"
                            disabled={isLoading}
                            title="Editar reserva"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteReservation(reservation._id)}
                            className="reservas-action-icon reservas-delete-icon"
                            disabled={isLoading}
                            title="Eliminar reserva"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}